import { type Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { Icon } from "icons";
import { type ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  TabsTrigger,
  cn,
} from "ui";

import { AlternateRemoveDialog } from "src/components/AlternateRemoveDialog";
import { DropIndicator } from "src/components/DropIndicator";
import { useAlternateTabDragAndDrop } from "src/workouts/useAlternateTabDragAndDrop";

export interface AlternateTabLabels {
  markAsMain: string;
  remove: string;
  removeTitle: string;
  removeDescription: string;
}

interface AlternateTabProps {
  alternateId: string;
  index: number;
  dndInstanceId: string;
  canEdit: boolean;
  isPending: boolean;
  dropIndicatorEdge: Edge | null;
  labels: AlternateTabLabels;
  children: ReactNode;
  onMakeMain: (alternateId: string) => void;
  onRemove: (alternateId: string) => void;
}

// A draggable alternate tab with a context menu to mark it as main or remove it.
export function AlternateTab(props: AlternateTabProps) {
  const tabRef = useRef<HTMLDivElement>(null);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const alternateId = props.alternateId;
  const canDrag = props.canEdit && !props.isPending;
  const dragState = useAlternateTabDragAndDrop({
    tabRef,
    alternateId,
    index: props.index,
    dndInstanceId: props.dndInstanceId,
    canDrag,
  });

  return (
    <>
      {/* Non-modal, so opening the remove dialog from it doesn't leave the
          page unclickable. */}
      <ContextMenu modal={false}>
        <ContextMenuTrigger asChild disabled={!canDrag}>
          <div
            ref={tabRef}
            className={cn("relative flex h-full items-center", {
              "opacity-50": dragState.type !== "idle",
              "cursor-grab": canDrag,
            })}
          >
            <TabsTrigger value={alternateId}>{props.children}</TabsTrigger>
            {/* Tabs touch, so there is no gap to center the line in. */}
            {props.dropIndicatorEdge ? (
              <DropIndicator edge={props.dropIndicatorEdge} gap={0} />
            ) : null}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            onSelect={() => {
              props.onMakeMain(alternateId);
            }}
          >
            {props.labels.markAsMain}
          </ContextMenuItem>
          <ContextMenuItem
            className="gap-2 text-destructive focus:text-destructive"
            onSelect={() => {
              setIsRemoveDialogOpen(true);
            }}
          >
            <Icon name="tabler-trash" className="size-4" />
            {props.labels.remove}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      <AlternateRemoveDialog
        open={isRemoveDialogOpen}
        title={props.labels.removeTitle}
        description={props.labels.removeDescription}
        removeLabel={props.labels.remove}
        onOpenChange={setIsRemoveDialogOpen}
        onRemove={() => {
          props.onRemove(alternateId);
        }}
      />
      {dragState.type === "preview"
        ? createPortal(
            <div className="flex items-center gap-1.5 rounded-md border bg-background px-1.5 py-0.5 text-sm font-medium text-foreground shadow-sm">
              {props.children}
            </div>,
            dragState.container,
          )
        : null}
    </>
  );
}
